/**
 * The typed fetch layer. Every network call in the app goes through `request`,
 * so cookie credentials, JSON handling and error shape are decided once.
 *
 * Errors from the API are `{ error, message }` per docs/API-CONTRACT.md. They
 * are rethrown as `ApiError`, which keeps the machine-readable `code` next to
 * the human sentence, because several flows branch on the code (a 409
 * `unavailable_staff` offers a force, a 422 `tax_table_unverified` explains
 * itself, a 401 sends you to the login page) while still showing the message.
 *
 * A network failure (the API not running at all) is deliberately given the same
 * shape with code `network`, so no screen has to distinguish "server said no"
 * from "server was not there" before it can render something readable. Never a
 * white screen.
 */

import type { IsoDate, Hhmm } from './time.ts';

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly body: unknown;
  constructor(code: string, message: string, status: number, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.body = body;
  }
  get isAuth(): boolean {
    return this.status === 401;
  }
  get isOffline(): boolean {
    return this.code === 'network';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      credentials: 'include',
      headers: init.body ? { 'content-type': 'application/json', ...(init.headers ?? {}) } : (init.headers ?? {}),
      ...init,
    });
  } catch {
    throw new ApiError(
      'network',
      'Cannot reach the Sneaky Panda server. Check your connection and try again.',
      0,
    );
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
  }

  if (!res.ok) {
    const b = parsed as { error?: string; message?: string } | null;
    throw new ApiError(
      b?.error ?? `http_${res.status}`,
      b?.message ?? `The server returned an error (${res.status}).`,
      res.status,
      parsed,
    );
  }
  return parsed as T;
}

const get = <T>(path: string) => request<T>(path);
const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });
const patch = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'PATCH', body: body === undefined ? undefined : JSON.stringify(body) });
const del = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'DELETE', body: body === undefined ? undefined : JSON.stringify(body) });

// ---------------------------------------------------------------------------
// Shared types. Names and shapes follow docs/API-CONTRACT.md; anything the
// contract leaves implicit is typed optional so a missing field degrades to a
// quieter UI rather than a crash.
// ---------------------------------------------------------------------------

/** `dev` is Sneaky Panda support, not a person the business employs. */
export type Role = 'owner' | 'manager' | 'staff' | 'dev';
export type ShiftStatus = 'draft' | 'published';
export type TimesheetStatus = 'active' | 'submitted' | 'approved' | 'adjusted';
export type PayRunStatus = 'draft' | 'finalised';
export type PayBucket = 'ordinary' | 'saturday' | 'sunday' | 'public_holiday' | 'adjustment';

export interface User {
  id: string;
  name: string;
  email: string;
}

export interface Membership {
  tenantId: string;
  tenantSlug: string;
  tenantName: string;
  role: Role;
}

export interface MeResponse {
  user: User;
  memberships: Membership[];
}

export interface StaffMember {
  userId: string;
  name: string;
  email: string;
  phone: string | null;
  role: Role;
  active: boolean;
  employmentType: string;
  rateOrdinary: string | number;
  rateSaturday: string | number;
  rateSunday: string | number;
  claimsTaxFreeThreshold: boolean;
  tfnProvided?: boolean;
  superFundName?: string | null;
  superMemberNumber?: string | null;
  /** Has set a password and can sign in. */
  hasAccount?: boolean;
  /** Invited, has not accepted yet. NOT the same as archived. */
  invitePending?: boolean;
}

export interface Area {
  id: string;
  name: string;
  colour: string | null;
  position: number;
  active: boolean;
}

export interface ShiftTemplate {
  id: string;
  name: string;
  startTime: Hhmm;
  endTime: Hhmm;
  colour: string | null;
  position: number;
  active: boolean;
}

export interface Shift {
  id: string;
  areaId: string | null;
  userId: string | null;
  date: IsoDate;
  startTime: Hhmm;
  endTime: Hhmm;
  breakMinutes: number;
  status: ShiftStatus;
  locked: boolean;
  notes: string | null;
  /** Present for a boss only. Staff responses omit it, per the contract. */
  cost?: number;
  /** Optional server convenience; the client recomputes it either way. */
  minutes?: number;
  /** The rostered person cannot work it. It stays theirs until covered. */
  needsCover?: boolean;
  coverReason?: string | null;
}

export interface AvailableShift {
  id: string;
  date: IsoDate;
  startTime: Hhmm;
  endTime: Hhmm;
  breakMinutes: number;
  areaId: string | null;
  needsCover: boolean;
  reason: string | null;
  open: boolean;
  claimedByMe: boolean;
  claimCount: number;
}

export interface UnavailabilityRecord {
  id: string;
  userId: string;
  date: IsoDate;
  allDay: boolean;
  startTime: Hhmm | null;
  endTime: Hhmm | null;
  note: string | null;
}

export interface RosterTotals {
  byDate: Record<string, { minutes: number; cost: number }>;
  byUser: Record<string, { minutes: number; cost: number }>;
  totalMinutes: number;
  totalCost: number;
}

export interface RosterResponse {
  week: IsoDate;
  areas: Area[];
  templates: ShiftTemplate[];
  staff: StaffMember[];
  shifts: Shift[];
  unavailability: UnavailabilityRecord[];
  /** Boss only. Absent for staff, and the UI must show no money at all then. */
  totals?: RosterTotals;
}

export interface Timesheet {
  id: string;
  userId: string;
  shiftId: string | null;
  date: IsoDate;
  clockOn: string;
  clockOff: string | null;
  breakMinutes: number;
  status: TimesheetStatus;
  approvedBy?: string | null;
  approvedAt?: string | null;
  notes: string | null;
  /** Variance fields the contract promises on the boss timesheet list. */
  rosteredMinutes?: number | null;
  clockedMinutes?: number | null;
  varianceMinutes?: number | null;
  rosteredStart?: Hhmm | null;
  rosteredEnd?: Hhmm | null;
  userName?: string;
}

export interface PayRun {
  id: string;
  periodStart: IsoDate;
  periodEnd: IsoDate;
  status: PayRunStatus;
  createdAt: string;
  finalisedAt: string | null;
  /** Summary fields, if the API sends them on the list. */
  staffCount?: number;
  totalGross?: number | string;
  totalNet?: number | string;
  totalTax?: number | string;
  totalSuper?: number | string;
}

export interface PayslipLine {
  id?: string;
  bucket: PayBucket;
  label: string;
  hours: number | string;
  rate: number | string;
  amount: number | string;
  position?: number;
}

export interface Payslip {
  id: string;
  payRunId: string;
  userId: string;
  userName?: string;
  gross: number | string;
  tax: number | string;
  net: number | string;
  superAmount: number | string;
  ytdGross: number | string;
  ytdTax: number | string;
  ytdSuper: number | string;
  createdAt?: string;
  lines?: PayslipLine[];
  /** Enough tenant context to print a compliant payslip without a second call. */
  periodStart?: IsoDate;
  periodEnd?: IsoDate;
  issuedAt?: string;
  status?: PayRunStatus;
  tenantName?: string;
  tenantAbn?: string | null;
  tenantAddress?: string | null;
  superFundName?: string | null;
  superMemberNumber?: string | null;
}

/**
 * The detail response is the run's own fields with its payslips alongside,
 * NOT a nested `{ run }`. It was declared nested once and the page crashed on
 * every draft, because a response type is an unchecked assertion over the wire
 * and the compiler cannot see the difference.
 */
export type PayRunDetail = PayRun & { payslips: Payslip[] };

export interface DingRoutineEntry {
  event: string;
  offsetHours: number;
}

export interface TenantSettings {
  unavailabilityNoticeDays: number;
  superRatePercent: number;
  weekStartsOn?: number;
  dingRoutine: DingRoutineEntry[];
}

export interface SettingsResponse {
  tenant: {
    id: string;
    slug: string;
    name: string;
    businessType: string;
    abn: string | null;
    address: string | null;
    phone: string | null;
    logoUrl: string | null;
    timezone: string;
  };
  settings: TenantSettings;
  templates: ShiftTemplate[];
  areas: Area[];
}

export interface AppNotification {
  id: string;
  type: string;
  payload: Record<string, unknown> & { title?: string; body?: string; week?: string; date?: string };
  readAt: string | null;
  createdAt: string;
}

export interface DingPrefs {
  extraDings: DingRoutineEntry[];
  /** The boss routine, read-only for staff. Shown next to their own additions. */
  tenantRoutine?: DingRoutineEntry[];
}

interface Items<T> {
  items: T[];
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

const t = (slug: string) => `/api/t/${encodeURIComponent(slug)}`;

export const api = {
  // --- auth ---------------------------------------------------------------
  login: (email: string, password: string) => post<MeResponse>('/api/auth/login', { email, password }),
  /** Dev only. Guarded at the call site by import.meta.env.DEV so it is not in
   *  a production bundle at all. Not part of the v1 contract; the API exposes it
   *  behind its own NODE_ENV check. */
  devLogin: (email: string) => post<MeResponse>('/api/auth/dev-login', { email }),
  logout: () => post<void>('/api/auth/logout'),
  me: () => get<MeResponse>('/api/me'),
  acceptInvite: (token: string, password: string) =>
    post<MeResponse>('/api/auth/accept-invite', { token, password }),
  requestReset: (email: string) => post<{ ok: boolean; message: string }>('/api/auth/request-reset', { email }),
  resetPassword: (token: string, password: string) =>
    post<MeResponse>('/api/auth/reset-password', { token, password }),

  // --- staff --------------------------------------------------------------
  staff: (slug: string) => get<Items<StaffMember>>(`${t(slug)}/staff`),
  inviteStaff: (slug: string, body: { name: string; email: string; role?: Role; force?: boolean }) =>
    post<{ userId: string; inviteUrl: string }>(`${t(slug)}/staff`, body),
  updateStaff: (slug: string, userId: string, body: Partial<StaffMember>) =>
    patch<StaffMember>(`${t(slug)}/staff/${userId}`, body),

  restoreShift: (slug: string, body: Partial<Shift>) => post<Shift>(`${t(slug)}/shifts/restore`, body),

  pushTest: (slug: string) =>
    post<{ ok: boolean; devices: number; message: string }>(`${t(slug)}/push/test`),

  // --- shift cover ---------------------------------------------------------
  requestCover: (slug: string, shiftId: string, reason?: string) =>
    post<{ ok: boolean; message?: string }>(`${t(slug)}/shifts/${shiftId}/cover`, { reason }),
  cancelCover: (slug: string, shiftId: string) => del<{ ok: boolean }>(`${t(slug)}/shifts/${shiftId}/cover`),
  availableShifts: (slug: string) => get<Items<AvailableShift>>(`${t(slug)}/shifts/available`),
  claimShift: (slug: string, shiftId: string) => post<{ ok: boolean }>(`${t(slug)}/shifts/${shiftId}/claim`),
  unclaimShift: (slug: string, shiftId: string) => del<{ ok: boolean }>(`${t(slug)}/shifts/${shiftId}/claim`),
  shiftClaims: (slug: string, shiftId: string) =>
    get<Items<{ userId: string; name: string; at: string }>>(`${t(slug)}/shifts/${shiftId}/claims`),

  // --- roster -------------------------------------------------------------
  roster: (slug: string, week: IsoDate) => get<RosterResponse>(`${t(slug)}/roster?week=${week}`),
  createShift: (
    slug: string,
    body: {
      date: IsoDate;
      startTime: Hhmm;
      endTime: Hhmm;
      areaId?: string | null;
      userId?: string | null;
      breakMinutes?: number;
      notes?: string | null;
      force?: boolean;
    },
  ) => post<Shift>(`${t(slug)}/shifts`, body),
  updateShift: (
    slug: string,
    id: string,
    body: Partial<Pick<Shift, 'date' | 'startTime' | 'endTime' | 'areaId' | 'userId' | 'breakMinutes' | 'notes' | 'locked'>> & {
      force?: boolean;
      unlock?: boolean;
    },
  ) => patch<Shift>(`${t(slug)}/shifts/${id}`, body),
  deleteShift: (slug: string, id: string) => del<{ ok: boolean; restore: Shift }>(`${t(slug)}/shifts/${id}`),
  lockShift: (slug: string, id: string) => post<Shift>(`${t(slug)}/shifts/${id}/lock`),
  unlockShift: (slug: string, id: string) => post<Shift>(`${t(slug)}/shifts/${id}/unlock`),
  publishWeek: (slug: string, week: IsoDate) =>
    post<{ count: number }>(`${t(slug)}/roster/publish`, { week }),
  copyWeek: (slug: string, fromWeek: IsoDate, toWeek: IsoDate) =>
    post<{ copied: number; madeOpen: Array<{ date: IsoDate; name?: string; userId?: string }> }>(
      `${t(slug)}/roster/copy-week`,
      { fromWeek, toWeek },
    ),

  // --- availability -------------------------------------------------------
  availability: (slug: string, from: IsoDate, to: IsoDate) =>
    get<Items<UnavailabilityRecord>>(`${t(slug)}/availability?from=${from}&to=${to}`),
  markUnavailable: (slug: string, body: { date: IsoDate; note?: string; userId?: string }) =>
    post<UnavailabilityRecord>(`${t(slug)}/availability`, body),
  clearUnavailable: (slug: string, id: string) => del<void>(`${t(slug)}/availability/${id}`),

  // --- clock and timesheets ----------------------------------------------
  clockOn: (slug: string, shiftId?: string) => post<Timesheet>(`${t(slug)}/clock/on`, { shiftId }),
  /**
   * The staff shell's "am I currently clocked on" check. Not in the v1
   * contract, which only exposes the boss-scoped timesheet list, so the clock
   * screen treats a 404 here as "no endpoint" and falls back to what it
   * remembered locally rather than showing an error.
   */
  clockCurrent: (slug: string) => get<{ timesheet: Timesheet | null }>(`${t(slug)}/clock/current`),
  clockOff: (slug: string, breakMinutes?: number) => post<Timesheet>(`${t(slug)}/clock/off`, { breakMinutes }),
  timesheets: (slug: string, week: IsoDate) => get<Items<Timesheet>>(`${t(slug)}/timesheets?week=${week}`),
  updateTimesheet: (
    slug: string,
    id: string,
    body: { clockOn?: string; clockOff?: string; breakMinutes?: number; notes?: string; approve?: boolean },
  ) => patch<Timesheet>(`${t(slug)}/timesheets/${id}`, body),

  // --- pay ----------------------------------------------------------------
  payRuns: (slug: string) => get<Items<PayRun>>(`${t(slug)}/payruns`),
  payRun: (slug: string, id: string) => get<PayRunDetail>(`${t(slug)}/payruns/${id}`),
  createPayRun: (slug: string, periodStart: IsoDate, periodEnd: IsoDate) =>
    post<PayRunDetail>(`${t(slug)}/payruns`, { periodStart, periodEnd }),
  finalisePayRun: (slug: string, id: string) => post<PayRun>(`${t(slug)}/payruns/${id}/finalise`),
  myPayslips: (slug: string) => get<Items<Payslip>>(`${t(slug)}/my/payslips`),
  payslip: (slug: string, id: string) => get<Payslip>(`${t(slug)}/payslips/${id}`),

  // --- settings -----------------------------------------------------------
  settings: (slug: string) => get<SettingsResponse>(`${t(slug)}/settings`),
  /**
   * The staff-safe slice of tenant settings.
   *
   * `GET /settings` is boss-gated, and a staff member genuinely needs two
   * values from it: the unavailability notice window (so the calendar can
   * disable days that the server is going to refuse, and say why BEFORE they
   * tap) and the tenant ding routine (shown read-only next to their own extra
   * dings). Not in the v1 contract yet, so every caller treats 403 and 404 as
   * "not offered" and degrades rather than erroring.
   */
  mySettings: (slug: string) =>
    get<{ unavailabilityNoticeDays: number; dingRoutine?: DingRoutineEntry[] }>(`${t(slug)}/my/settings`),
  updateSettings: (
    slug: string,
    body: Partial<SettingsResponse['tenant']> & { settings?: Partial<TenantSettings> },
  ) => patch<SettingsResponse>(`${t(slug)}/settings`, body),
  createTemplate: (slug: string, body: Partial<ShiftTemplate>) =>
    post<ShiftTemplate>(`${t(slug)}/templates`, body),
  updateTemplate: (slug: string, id: string, body: Partial<ShiftTemplate>) =>
    patch<ShiftTemplate>(`${t(slug)}/templates/${id}`, body),
  deleteTemplate: (slug: string, id: string) => del<void>(`${t(slug)}/templates/${id}`),
  createArea: (slug: string, body: Partial<Area>) => post<Area>(`${t(slug)}/areas`, body),
  updateArea: (slug: string, id: string, body: Partial<Area>) => patch<Area>(`${t(slug)}/areas/${id}`, body),
  deleteArea: (slug: string, id: string) => del<void>(`${t(slug)}/areas/${id}`),

  // --- notifications and push --------------------------------------------
  notifications: (slug: string, unreadOnly = false) =>
    get<Items<AppNotification>>(`${t(slug)}/notifications${unreadOnly ? '?unread=1' : ''}`),
  markNotificationsRead: (slug: string, ids?: string[]) =>
    post<{ updated: number }>(`${t(slug)}/notifications/read`, ids ? { ids } : {}),
  dingPrefs: (slug: string) => get<DingPrefs>(`${t(slug)}/ding-prefs`),
  updateDingPrefs: (slug: string, extraDings: DingRoutineEntry[]) =>
    patch<DingPrefs>(`${t(slug)}/ding-prefs`, { extraDings }),
  pushSubscribe: (slug: string, sub: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
    post<void>(`${t(slug)}/push/subscribe`, sub),
  pushUnsubscribe: (slug: string, endpoint: string) =>
    del<void>(`${t(slug)}/push/subscribe`, { endpoint }),
  /** Optional. The push screen degrades to "not available" when this 404s
   *  rather than presenting a button that cannot work. */
  /** Returns `{ key }`, per the contract addendum. Declaring it as
   *  `publicKey` here made every attempt to enable notifications throw. */
  vapidKey: () => get<{ key: string | null }>('/api/push/vapid-public-key'),
};

// ---------------------------------------------------------------------------
// Query keys, in one place so an invalidation cannot miss by a typo.
// ---------------------------------------------------------------------------
export const qk = {
  me: ['me'] as const,
  roster: (slug: string, week: string) => ['roster', slug, week] as const,
  staff: (slug: string) => ['staff', slug] as const,
  timesheets: (slug: string, week: string) => ['timesheets', slug, week] as const,
  payRuns: (slug: string) => ['payruns', slug] as const,
  payRun: (slug: string, id: string) => ['payrun', slug, id] as const,
  availableShifts: (slug: string) => ['available-shifts', slug] as const,
  shiftClaims: (slug: string, shiftId: string) => ['shift-claims', slug, shiftId] as const,
  myPayslips: (slug: string) => ['my-payslips', slug] as const,
  payslip: (slug: string, id: string) => ['payslip', slug, id] as const,
  settings: (slug: string) => ['settings', slug] as const,
  mySettings: (slug: string) => ['my-settings', slug] as const,
  availability: (slug: string, from: string, to: string) => ['availability', slug, from, to] as const,
  notifications: (slug: string) => ['notifications', slug] as const,
  dingPrefs: (slug: string) => ['ding-prefs', slug] as const,
};
